import * as links from "~/components/organisms/navigation-bar/links";
import {
    DoubleColumnLinkList,
    GrayContainer,
    LinkList,
    SectionContainer,
    SubSectionContainer,
} from "~/components/organisms/navigation-bar/shared";
import shopLogo from "~/assets/shop-img-academy-logo-sm-b.svg";
import premierePartner from "~/assets/premier-partners-nav-img-g-ua.webp";
import consultIcon from "~/assets/icon-consult.svg";
import callIcon from "~/assets/icon-call.svg";
import chatIcon from "~/assets/icon-chat.svg";
import Image from "~/components/atoms/Image";
import poweredByNCSA from "~/assets/powered-by-ncsa.svg";
import { cn } from "~/lib/utils";
function IconWithText({ src, text }: { src: string; text: string }) {
    return (
        <div className="flex flex-col items-center gap-2">
            <Image loading="lazy" src={src} alt={text} className="h-[40px]" />
            <div className="text-center text-[14px] font-semibold text-blue-80">{text}</div>
        </div>
    );
}
export function Connect() {
    return (
        <GrayContainer>
            <div className="flex flex-row items-center justify-evenly gap-2">
                <IconWithText src={consultIcon} text="Request Consult" />
                <IconWithText src={callIcon} text="Call Us" />
                <IconWithText src={chatIcon} text="Start A Chat" />
            </div>
        </GrayContainer>
    );
}

export function PremierePartners() {
    return (
        <a href="https://www.imgacademy.com/about-img-academy/sponsors" className="py-8">
            <Image
                loading="lazy"
                alt="premiere partners"
                className="w-full"
                src={premierePartner}
            />
        </a>
    );
}

export function PopularLinks() {
    return (
        <SectionContainer title="Popular Links">
            <div className="p-[20px]">
                <LinkList links={links.popularLinks} />
                <a href="https://shop.imgacademy.com">
                    <Image src={shopLogo} alt="shop" className="max-w-[70px] py-2" />
                </a>
            </div>
        </SectionContainer>
    );
}

export function BoardingSchool() {
    return (
        <SectionContainer
            title="Boarding School"
            href="https://www.imgacademy.com/boarding-school"
            underlineOnHover
        >
            <div className="flex flex-col gap-2">
                <LinkList links={links.boardingSchool} />
                <SubSectionContainer title="Athletics">
                    <GrayContainer>
                        <DoubleColumnLinkList
                            primaryColumn={{
                                title: "Girls",
                                links: links.athleticSports.girls,
                            }}
                            secondaryColumn={{
                                title: "Boys",
                                links: links.athleticSports.boys,
                            }}
                        />
                    </GrayContainer>
                </SubSectionContainer>
            </div>
        </SectionContainer>
    );
}

export function OtherExperiences() {
    return (
        <SubSectionContainer title="Other Experiences">
            <LinkList links={links.otherExperiencesCamps} />
        </SubSectionContainer>
    );
}

export function CampusSports() {
    return (
        <SubSectionContainer title="Sports">
            <GrayContainer>
                <DoubleColumnLinkList
                    primaryColumn={{
                        title: "Girls",
                        links: links.sportCamps.girls,
                    }}
                    secondaryColumn={{
                        title: "Boys",
                        links: links.sportCamps.boys,
                    }}
                />
            </GrayContainer>
        </SubSectionContainer>
    );
}

export function SportCamps() {
    return (
        <SectionContainer
            title="Sport Camps"
            href="https://www.imgacademy.com/sport-camps"
            underlineOnHover
        >
            <div className="flex flex-col gap-2">
                <a
                    href="https://www.imgacademy.com/sport-camps/campus-life"
                    className="text-[17px] text-blue-80 hover:underline"
                >
                    Activities & Campus Life
                </a>
                <a
                    href="https://www.imgacademy.com/special-group-rates"
                    className="text-[17px] text-blue-80 hover:underline"
                >
                    Special Rates & Group Benefits
                </a>
                <CampusSports />
            </div>
        </SectionContainer>
    );
}
export function DevelopmentalCoaching() {
    return (
        <SectionContainer
            title="Developmental Coaching"
            href="https://plus.imgacademy.com/developmental-coaching/"
            underlineOnHover
        >
            <LinkList links={links.developmentalCoaching} />
        </SectionContainer>
    );
}

export function MembershipTypes() {
    const className = "cursor-pointer py-1 font-normal text-blue-80";
    return (
        <SubSectionContainer title="Membership Types">
            <div className="flex flex-col gap-4">
                <div className="flex flex-col">
                    <a
                        className={cn(className, "hover:underline")}
                        href="https://plus.imgacademy.com/memberships/essentials"
                    >
                        Essentials: <span className="italic">On-Demand Resources</span>
                    </a>
                    <a
                        className={className}
                        href="https://plus.imgacademy.com/memberships/essentials"
                    >
                        <span className="underline">Learn More</span>
                        {" >"}
                    </a>
                </div>
                <div className="flex flex-col">
                    <a
                        className="py-1 font-normal text-blue-80 hover:underline"
                        href="https://plus.imgacademy.com/memberships/one-on-one"
                    >
                        One-On-One: <span className="italic">Personalized, Live Coaching</span>
                    </a>
                    {/* TODO: Add this link back in once marketing has their site up */}
                    {/* <a className={className}>
                        <span className="underline">Get a Consultation</span>
                        {" >"}
                    </a> */}
                </div>
            </div>
        </SubSectionContainer>
    );
}

export function CollegeRecruiting({ tripleColumns }: { tripleColumns?: boolean }) {
    const Section = ({ title, sports }: { title: string; sports: links.LinkItem[] }) => {
        const rowsFor3Col = Math.ceil(sports.length / 3);
        const rowsFor2Col = Math.ceil(sports.length / 2);
        const rows = tripleColumns ? rowsFor3Col : rowsFor2Col;
        return (
            <div>
                <div className="text-[15px] font-bold text-blue-80">{title}</div>
                <div
                    className={cn(`grid grid-flow-col gap-x-4`)}
                    style={{
                        gridTemplateRows: `repeat(${rows}, 1fr)`,
                    }}
                >
                    {sports.map(sport => (
                        <a
                            key={`${title}-${sport.label}`}
                            href={sport.href}
                            className="min-w-[120px] max-w-[213px] py-1 font-normal text-blue-80 hover:underline"
                        >
                            {sport.label}
                        </a>
                    ))}
                </div>
            </div>
        );
    };
    return (
        <div className="flex flex-col">
            <a
                className="-mr-7 mb-4 flex flex-row items-center gap-3 text-[20px] font-bold text-blue-80 hover:underline"
                href="https://plus.imgacademy.com/college-recruiting"
            >
                College Recruiting
                <Image
                    loading="lazy"
                    src={poweredByNCSA}
                    alt="powered by ncsa"
                    className="mt-1 w-[128px]"
                />
            </a>
            <SubSectionContainer title="College Search">
                <GrayContainer>
                    <div className="flex flex-col gap-4">
                        <Section
                            title="Women's Sports"
                            sports={links.collegeRecruitingSports.womens}
                        />
                        <Section title="Men's Sports" sports={links.collegeRecruitingSports.mens} />
                        <Section title="Coed Sports" sports={links.collegeRecruitingSports.mixed} />
                    </div>
                </GrayContainer>
            </SubSectionContainer>
            <a
                href="/college-recruiting/signup"
                className="py-2 text-[15px] text-blue-80 underline"
            >
                {`Create Your NCSA Profile >`}
            </a>
        </div>
    );
}
